import { useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { useField } from 'informed';
import { LoadingIndicator } from 'components/common';
import { Icon } from 'components/common/icons';

type OptionalProps =
  | { encoding: string; dataAsString: true }
  | { encoding?: string; dataAsString?: false };

type Props = OptionalProps & {
  allowedFileTypes?: string[];
  field: string;
  getInputRef?: (el: HTMLInputElement) => void;
  id: string;
};

const ReadLocalFile = (props: Props) => {
  const { messages, theme } = useReduxState();

  const [error, setError] = useState(false);
  const [fieldState, setFieldState] = useState<string>(null);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const fileReader = useRef<FileReader>(new FileReader());

  const { fieldApi, render, userProps } = useField<ArrayBuffer | string>(props);

  const { allowedFileTypes, dataAsString, encoding, getInputRef, id } =
    userProps;

  useEffect(() => {
    if (getInputRef && inputRef.current) getInputRef(inputRef.current);
    inputRef.current?.addEventListener('change', onFileChange);
    fileReader.current?.addEventListener('error', onReaderError);
    fileReader.current?.addEventListener('load', onReaderSuccess);

    return () => {
      inputRef.current?.removeEventListener('change', onFileChange);
      fileReader.current?.removeEventListener('error', onReaderError);
      fileReader.current?.removeEventListener('load', onReaderSuccess);
    };
  }, []);

  const handleResetField = (ev: React.MouseEvent) => {
    ev.preventDefault();

    setFieldState(null);
    fieldApi.setValue(null);
  };

  const onFileChange = () => {
    const files = inputRef.current.files;
    if (!files.length) return;

    const file = files[0];
    if (validateFileType(file.name)) {
      setLoading(true);
      if (dataAsString) fileReader.current.readAsText(file, encoding);
      else fileReader.current.readAsArrayBuffer(file);
    } else {
      setFieldState(messages.uploaderFileTypeError);
      setError(true);
    }
  };

  const onReaderError = () => {
    setFieldState(messages.editorImportError);
    setLoading(false);
    setError(true);
  };

  const onReaderSuccess = () => {
    fieldApi.setValue(fileReader.current.result);
    setFieldState(inputRef.current.files[0].name);
    setLoading(false);

    inputRef.current.value = '';
  };

  const validateFileType = (filename: string) => {
    if (!allowedFileTypes.length) return true;

    const extension = filename.split('.').pop();
    if (allowedFileTypes.includes(`.${extension}`)) return true;
    else return false;
  };

  return render(
    <div className="read-file">
      <label className="input" htmlFor={`local-file-read-${id}`}>
        {loading ? (
          <>
            {`${messages.tasksReading}...`}

            <LoadingIndicator size={12} padding={0} style={{ marginLeft: 5 }} />
          </>
        ) : (
          <div className="input-name">
            <Icon icon={['far', 'file']} style={{ fontSize: '1.4rem' }} />

            {fieldState || messages.uploaderChooseFile}

            {fieldState ? (
              <div className="reset" onClick={handleResetField}>
                <Icon icon="times" style={{ marginTop: 3 }} />
              </div>
            ) : null}
          </div>
        )}
      </label>

      <input
        accept={allowedFileTypes.length ? allowedFileTypes.join(',') : ''}
        disabled={Boolean(fieldState)}
        id={`local-file-read-${id}`}
        ref={inputRef}
        type="file"
      />

      <style jsx>{`
        .read-file {
          display: flex;
          flex-direction: row;
        }
        .input {
          align-items: center;
          background-color: ${error
            ? theme.dangerLightColor
            : theme.lightGreyDark};
          border-radius: 6px;
          color: ${error ? theme.dangerLightTextColor : theme.textColorLight};
          cursor: ${fieldState ? 'auto' : 'pointer'};
          display: flex;
          font-size: 1.4rem;
          font-weight: 700;
          padding: 8px 14px;
        }
        .read-file > input[type='file'] {
          display: none;
        }
        .input-name {
          align-items: center;
          display: flex;
          gap: 8px;
        }
        .reset {
          cursor: pointer;
          font-size: 1.3rem;
          position: relative;
        }
      `}</style>
    </div>
  );
};

export default ReadLocalFile;
